import { useLayerStore } from '@/store/use-layer-store';
import { ShapeUtils } from './shape-util';
import ShapeAttribute from './attributes';
import generateID from '@/utils/generate-id';
import { EventType, emitter } from '@/utils/emitter.utils';
import { ShapeBaseAttribute } from './attributes/base';
import { ProgressBarAttribute } from './attributes/progress-bar';
import { TextAttribute } from './attributes/text';
import { WidgetAttribute } from './attributes/widget';
import { SlotAttribute } from './attributes/slot';
import { ImageAttribute } from './attributes/image';
import { ImageArrayAttribute } from './attributes/image-array';
import { AppWidgetAttribute } from './attributes/app-widget';
import { SpriteAttribute } from './attributes/sprite';
import { cloneDeep } from 'lodash-es';
import { useEditorStore } from '@/store/use-editor-store';
import { TextWatchfaceAttribute } from './attributes/text-watchface';
import { RiveAttribute } from './attributes/rive';
import { VideoWatchfaceAttribute } from './attributes/video-watchface';
import { canUse } from '@/utils/can-use.utils';

export enum ShapeType {
  /**
   * 进度条（直线或弧形）
   */
  ProgressBar = 'ProgressBar',
  /**
   * 文本
   */
  Text = 'Text',
  /**
   * 表达式
   */
  Widget = 'Widget',
  /**
   * 容器
   */
  Slot = 'Slot',
  /**
   * 单图或指针
   */
  Image = 'Image',
  /**
   * 图组（数字图片或图片数值对）
   */
  ImageArray = 'ImageArray',

  /**
   * 动画序列帧
   */
  Sprite = 'Sprite',

  /**
   * 非协议中类型，仅编辑器使用
   * @deprecated
   */
  Group = 'Group',

  /**
   * 相册或人像表盘背景
   */
  PhotoAlbumBg = 'PhotoAlbumBg',

  /**
   * 复杂程序（应用小部件）
   */
  AppWidget = 'AppWidget',
  /**
   * js文件
   */
  JsFile = 'JsFile',

  /**
   * 文本表盘
   */
  TextWatchface = 'TextWatchface',
  /**
   * Rive表盘
   */
  Rive = 'Rive',
  /**
   *
   * 视频表盘
   */
  VideoWatchface = 'VideoWatchface',

  /**
   * Apk程序
   */
  ApkFile = 'ApkFile'
}

const ShapeAttributeMap = {
  [ShapeType.ProgressBar]: ProgressBarAttribute,
  [ShapeType.Text]: TextAttribute,
  [ShapeType.Widget]: WidgetAttribute,
  [ShapeType.Slot]: SlotAttribute,
  [ShapeType.Image]: ImageAttribute,
  [ShapeType.ImageArray]: ImageArrayAttribute,
  [ShapeType.AppWidget]: AppWidgetAttribute,
  [ShapeType.Sprite]: SpriteAttribute,
  [ShapeType.TextWatchface]: TextWatchfaceAttribute,
  [ShapeType.Rive]: RiveAttribute,
  [ShapeType.VideoWatchface]: VideoWatchfaceAttribute
};

export class Shape extends ShapeAttribute {
  id: string;
  type: ShapeType;

  private _layer: StoreLayerItem;

  /**
   * 返回父级shape，如果为根目录下，则返回undefined
   *
   * @readonly
   * @type {(Shape | undefined)}
   * @memberof Shape
   */
  get parent(): Shape | undefined {
    const { parent } = useLayerStore().findLayer(this.id) ?? {};
    if (!parent || parent.type == 'normal' || parent.type == 'AOD') return;
    return Shape.getShape(parent.id);
  }

  get layer(): StoreLayerItem | undefined {
    const layer = this._layer ?? useLayerStore().findLayer(this.id)?.layer;
    this._layer = layer;
    return layer;
  }

  resizeEnabled() {
    // // 当不可拖拽位置时，默认为不可更改大小
    const { type, attrs } = this;

    if (attrs._isLocked) return;

    // 表达式可更改大小
    if (type == ShapeType.Widget) return true;

    // 启动自动布局的组可更改大小
    if (
      type == ShapeType.Group &&
      ['column', 'row'].includes(attrs.flexDirection ?? 'disabled')
    ) {
      return true;
    }

    // 固定宽高的普通文本可更改大小
    if (
      type === ShapeType.Text &&
      (this.attrs as TextAttribute).style !== 'arc' &&
      !this.attrs.sizeContent
    ) {
      return true;
    }

    if (type == ShapeType.TextWatchface) return true;

    return false;
  }

  get isPointerImage() {
    return this.type == ShapeType.Image && !!this.attrs.source;
  }

  get conditionEnabled() {
    return (
      !this.parent &&
      [
        ShapeType.Image,
        ShapeType.ImageArray,
        ShapeType.ProgressBar,
        ShapeType.Text,
        ShapeType.Sprite
      ].includes(this.type)
    );
  }

  /**
   * 是否需要通过widget拼接`数字填充`数组
   *
   * @readonly
   * @memberof Shape
   * @returns false:不需要widget；time:需要当做time类型处理；date:需要当做date类型处理
   */
  get widgetImageArray() {
    const { fillType } = this.attrs;
    if (this.type !== ShapeType.ImageArray || fillType !== '数字填充')
      return false;
    const res = this.sourceItem?.widgetImageArray;
    return res ? res : false;
  }

  /**
   * 图层是否可拖拽移动位置
   *
   * @readonly
   * @memberof Shape
   */
  draggable() {
    const { type, attrs } = this;
    const parnet = this.parent;
    // 已锁定不可移动
    if (attrs._isLocked)
      return {
        valid: false,
        msg: '元素已锁定，不可移动位置'
      };
    // 相册表盘不可移动
    if (type == ShapeType.PhotoAlbumBg)
      return {
        valid: false,
        msg: '人像/相册图层不可修改位置'
      };
    // 如果父容器为slot则不可移动
    if (parnet?.type == ShapeType.Slot)
      return {
        valid: false,
        msg: '容器内数据项不可修改位置'
      };
    // 如果父元素是有动效条件的组则不可移动
    if (parnet?.type == ShapeType.Widget && parnet.attrs.widgetConditions?.length)
      return {
        valid: false,
        msg: '动效组内子元素不可修改位置'
      };
    // 自动布局下的元素不可拖拽移动位置
    if (parnet?.isFlexLayout)
      return {
        valid: false,
        msg: '自动布局下子元素不可移动位置'
      };
    if (type == ShapeType.VideoWatchface) {
      return {
        valid: false,
        msg: '视频表盘不可修改位置'
      };
    }

    return {
      valid: true,
      msg: ''
    };
  }

  get canUseIFTTT() {
    return (
      this.parent == undefined &&
      !(this.attrs as any)._maskFlag &&
      (
        [
          ShapeType.Text,
          ShapeType.Image,
          ShapeType.ImageArray,
          ShapeType.ProgressBar,
          ShapeType.Sprite
        ].includes(this.type) ||
        // 组元素：子元素数量大于1时支持条件切换动效
        (this.type == ShapeType.Widget && this.canUseWidgetCondition)
      ) &&
      this.widgetImageArray == false
    );
  }

  /**
   * 组元素是否支持条件切换动效（子元素数量大于1）
   */
  get canUseWidgetCondition() {
    return (
      canUse('group-support-ifttt').valid &&
      this.parent == undefined &&
      this.type == ShapeType.Widget &&
      this.children.length > 1
    );
  }

  /**
   * 获取子元素
   *
   * @readonly
   * @type {Shape[]}
   * @memberof Shape
   */
  get children(): Shape[] {
    const res: Shape[] = [];

    this.layer?.children?.forEach((child) => {
      const shape = Shape.getShape(child.id);
      if (shape) res.push(shape);
    });

    return res;
  }

  // 缓存 shape 实例
  private static instances = new Map<string, Shape>();

  private constructor(layerItem: StoreLayerItem) {
    super();
    this.id = layerItem.id;
    this.type = layerItem.type as ShapeType;
    this._layer = layerItem;

    this.initAttrs();
    Shape.instances.set(this.id, this);
  }

  /**
   * 新创建一个元素，返回shape实例
   * @param parent 元素所属theme
   * @param type 元素类型
   * @param attrs 元素属性
   */
  static create(
    type: ShapeType,
    options?: {
      attrs?: Partial<ShapeAttribute['attrs']>;
      layer?: StoreLayerItem;
      parentShapeId?: string;
      theme?: StoreThemeItem;
    }
  ) {
    const { attrs, layer, parentShapeId, theme } = options ?? {};

    const layerItem: StoreLayerItem = layer
      ? cloneDeep(layer)
      : {
          id: generateID(),
          type,
          attrs: {
            ...attrs,
            name: attrs?.name ?? ShapeUtils.getCNName(type)
          }
        };

    // 基本属性
    const ShapeAttribute =
      ShapeAttributeMap[type as keyof typeof ShapeAttributeMap];
    const baseAttrs = ShapeAttribute
      ? new ShapeAttribute()
      : new ShapeBaseAttribute();
    layerItem.attrs = {
      ...baseAttrs,
      ...layerItem.attrs
    };

    // 重置name
    if (!attrs?.name) {
      layerItem.attrs.name = ShapeUtils.generateShapeName(
        layerItem.type as ShapeType,
        parentShapeId
      );
    }

    // 插入
    const parentShape = Shape.getShape(parentShapeId ?? '');
    const parentLayer = parentShape?.layer ?? theme ?? useEditorStore().theme;
    if (!parentLayer) throw '无法创建Shape，parentShapeId不存在';
    if (!parentLayer.children?.length) parentLayer.children = [];

    // 相册表盘需要插入到顶部，其他的插入到最后
    if (
      [
        ShapeType.PhotoAlbumBg,
        ShapeType.TextWatchface,
        ShapeType.VideoWatchface
      ].includes(type)
    ) {
      // 相册/人像表盘尽可存在一个
      if (!parentLayer.children.find((item) => item.type == type)) {
        parentLayer.children.unshift(layerItem);
      }
    } else {
      // 如果顶部是rive,则需要像rive下一层插入
      const popItem = parentLayer.children.pop();
      if (popItem?.type == ShapeType.Rive) {
        parentLayer.children.push(layerItem, popItem);
      } else if (popItem?.attrs?._maskFlag) {
        // 如果顶部是全局蒙层，新元素插入到蒙层下面（倒数第二位）
        parentLayer.children.push(layerItem, popItem!);
      } else {
        if (popItem) {
          parentLayer.children.push(popItem, layerItem);
        } else {
          parentLayer.children.push(layerItem);
        }
      }
    }

    const shape = new this(layerItem);
    emitter.emit(EventType['shape:create'], shape.id);
    return shape;
  }

  /**
   * 获取shape
   * @params id
   */
  static getShape(id: string, _layer?: StoreLayerItem) {
    if (!id) return;

    if (Shape.instances.has(id)) return Shape.instances.get(id);

    const layer = _layer ?? useLayerStore().findLayer(id)?.layer;
    if (!layer) {
      console.log('获取shape错误,数据中不存在 id=' + id);
      return;
    }

    const shape = new this(layer);
    return shape;
  }

  /**
   * 删除元素
   */
  destroy() {
    emitter.emit(EventType['shape:delete-before']);
    const layerStore = useLayerStore();

    const layer = layerStore.findLayer(this.id);
    const { index, parent } = layer ?? {};
    if (layer?.layer.attrs.followingGroupName) {
      const allSlots = layerStore.themes.flatMap((theme) =>
        theme.children.filter((child) => child.type === 'Slot')
      );
      allSlots.forEach((slot) => {
        if (slot.attrs.slotFollowingId === this.id) {
          const slotShape = Shape.getShape(slot.id);
          slotShape?.destroy();
        }
      });
    }
    if (parent?.children?.length && index !== undefined) {
      parent.children.splice(index, 1);
    }

    // 删除子元素后，如果父组有条件切换动效且子元素不足2个，清除动效
    if (parent && parent.type === ShapeType.Widget) {
      const parentShape = Shape.getShape(parent.id);
      if (
        parentShape &&
        (parentShape.attrs as any).widgetConditions?.length > 0 &&
        (parent.children?.length ?? 0) <= 1
      ) {
        parentShape.setAttrs({ widgetConditions: undefined, widgetAnimations: undefined });
      }
    }

    Shape.instances.delete(this.id);

    emitter.emit(EventType['shape:deleted'], {
      shapeId: this.id,
      parentId: parent?.id
    });
  }

  /**
   * cache shapes
   */
  private static _shapeTree = new Map<string, Shape[]>();

  /**
   * 返回树形结构的shape数据
   */
  static shapeTree(themeId: string, cache = true) {
    const cachedData = this._shapeTree.get(themeId);
    if (cache && cachedData?.length) return cachedData;

    const layerStore = useLayerStore();
    const _shapes: Shape[] = [];
    layerStore.findTheme(themeId)?.children.forEach((layerItem) => {
      const shape = this.getShape(layerItem.id, layerItem);
      if (shape) _shapes.push(shape);
    });

    this._shapeTree.set(themeId, _shapes);
    return _shapes;
  }

  static clearCache() {
    this.instances.clear();
    this._shapeTree.clear();
  }

  // getClientRect() {
  //   const type = this.type;
  //   if (type == ShapeType.Slot) throw '不可获取slot的数据';
  //   // 如果是指针,需要获取指针旋转的面积
  //   if (this.isPointerImage) {
  //   }
  //   return { w: this.attrs.w ?? 0, h: this.attrs.h ?? 0 };
  // }
}
